/**
 * Google Identity Services (Sign In With Google) helper.
 * Requires VUE_APP_GOOGLE_CLIENT_ID (same web client ID as GOOGLE_CLIENT_ID on the server).
 *
 * google.accounts.id.initialize() must run only once per client_id — repeated calls
 * trigger [GSI_LOGGER] warnings and flaky callbacks (e.g. after locale toggle).
 */

export function getGoogleClientId() {
  return (process.env.VUE_APP_GOOGLE_CLIENT_ID || '').trim();
}

/** @type {((response: { credential?: string }) => void) | null} */
let _credentialHandler = null;

/** @type {string | null} */
let _initializedForClientId = null;

function loadScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('no window'));
      return;
    }
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[data-growboard-gis]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Google script error')));
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-growboard-gis', '1');
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(s);
  });
}

function _gisDispatch(response) {
  try {
    _credentialHandler?.(response);
  } catch (e) {
    console.warn('Google credential handler:', e);
  }
}

/**
 * Renders the official Google button inside container (clears container first).
 */
export async function mountGoogleSignInButton(containerEl, { locale, onCredential }) {
  const cid = getGoogleClientId();
  if (!cid || !containerEl) return false;
  await loadScript();
  const lang = locale === 'ru' ? 'ru' : 'en';

  _credentialHandler = onCredential;

  if (_initializedForClientId !== cid) {
    try {
      window.google.accounts.id.cancel();
    } catch (e) {
      /* ignore */
    }
    window.google.accounts.id.initialize({
      client_id: cid,
      callback: _gisDispatch,
      auto_select: false,
    });
    _initializedForClientId = cid;
  }

  containerEl.innerHTML = '';
  const w = Math.min(Math.max(containerEl.offsetWidth || 400, 280), 480);
  window.google.accounts.id.renderButton(containerEl, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    width: w,
    text: 'continue_with',
    locale: lang,
    shape: 'rectangular',
  });
  return true;
}
